// IELTS Mock 4 — Academic Reading
// Same shape as IELTS_DATA_READING in ../IeltsReading.js (3 passages, 40 questions).
const IELTS_MOCK4_READING = {
  title: "Academic Reading Test 4",
  passages: [
    {
      id: "p1",
      title: "Passage 1: The Return of the Sea Otter",
      text: `(A) Sea otters live along the coasts of the North Pacific, from northern Japan across to Alaska and down to California. Unlike whales and seals, they have no thick layer of fat to protect them from the cold water. Instead, they rely on the densest fur of any animal, with up to a million hairs on every square inch of skin. Keeping warm in this way uses a great deal of energy, and a sea otter must eat around a quarter of its own body weight every day simply to survive.

(B) It was this remarkable fur that almost led to the animal's extinction. From the middle of the eighteenth century, hunters from Russia, and later from Britain and the United States, killed sea otters in enormous numbers. Their skins were sold for high prices, mostly to wealthy buyers in China, where they were used to decorate clothing. By 1911, it is thought that only between one and two thousand sea otters remained, scattered in small groups across their former range. In that year, an international treaty finally made it illegal to hunt them.

(C) Recovery was slow at first. In California, the animals were believed to have died out completely, until a group of about fifty was discovered near Big Sur in 1938. Their descendants now number around three thousand. In Alaska, the recovery was much stronger. In the 1960s and 1970s, scientists also moved several hundred otters from Alaska to areas where they had disappeared, including the coasts of Washington State and British Columbia. These translocations were largely successful, and otters are now well established in both places.

(D) Scientists describe the sea otter as a 'keystone species', meaning that it has an influence on its environment far greater than its numbers would suggest. Sea otters eat sea urchins, which in turn feed on kelp, a type of large seaweed that forms underwater forests. Where otters are absent, urchin numbers rise rapidly and the kelp is destroyed, leaving bare areas of rock known as 'urchin barrens'. When otters return, they reduce the number of urchins and the kelp grows back. Kelp forests provide shelter for many species of fish, and they also absorb large amounts of carbon dioxide from the atmosphere.

(E) Not everyone is pleased to see the sea otter return. Shellfish fishermen complain that the animals eat crabs, clams and abalone that they would otherwise catch and sell, and in some parts of Alaska crab fisheries have declined sharply in areas where otters have become common. However, some economists argue that the value of healthy kelp forests, both for tourism and as nurseries for young fish, is greater than the losses suffered by the shellfish industry.

(F) Sea otters still face a number of threats. Oil spills are particularly dangerous, because oil destroys the insulation provided by the fur, and an otter covered in oil may quickly die of cold. The Exxon Valdez disaster in Alaska in 1989 killed several thousand otters. Disease is another concern: some otters in California have been infected by parasites from cat faeces, which are washed into the sea from towns and cities. In recent years, scientists have also recorded an increase in the number of otters killed by sharks, which appear to bite them by mistake.`,
      questions: [
        {
          id: "q1_6",
          instruction: "Questions 1-6: Do the following statements agree with the information given in Reading Passage 1? Choose TRUE, FALSE or NOT GIVEN.",
          type: "mcq",
          items: [
            {
              id: 1,
              text: "Sea otters have a thick layer of fat to protect them from the cold.",
              options: ["TRUE", "FALSE", "NOT GIVEN"],
              answer: "FALSE"
            },
            {
              id: 2,
              text: "Most sea otter skins were sold to buyers in Russia.",
              options: ["TRUE", "FALSE", "NOT GIVEN"],
              answer: "FALSE"
            },
            {
              id: 3,
              text: "The 1911 treaty was signed by four countries.",
              options: ["TRUE", "FALSE", "NOT GIVEN"],
              answer: "NOT GIVEN"
            },
            {
              id: 4,
              text: "At one time, sea otters were thought to have disappeared from California.",
              options: ["TRUE", "FALSE", "NOT GIVEN"],
              answer: "TRUE"
            },
            {
              id: 5,
              text: "Most attempts to move otters to new areas in the 1960s and 1970s failed.",
              options: ["TRUE", "FALSE", "NOT GIVEN"],
              answer: "FALSE"
            },
            {
              id: 6,
              text: "Tourists are more likely to visit places where sea otters live.",
              options: ["TRUE", "FALSE", "NOT GIVEN"],
              answer: "NOT GIVEN"
            }
          ]
        },
        {
          id: "q7_13",
          instruction: "Questions 7-13: Complete the notes below. Write ONLY ONE WORD from the passage for each answer.",
          type: "fill_blank",
          items: [
            { id: 7, text: "To stay warm, a sea otter must eat about a ______ of its body weight each day.", answer: "quarter" },
            { id: 8, text: "Sea otters eat sea ______, which feed on kelp.", answer: ["urchins", "urchin"] },
            { id: 9, text: "Areas of rock where the kelp has been destroyed are called urchin ______.", answer: "barrens" },
            { id: 10, text: "Kelp forests absorb large amounts of carbon ______.", answer: "dioxide" },
            { id: 11, text: "Fishermen complain that otters eat crabs, clams and ______.", answer: "abalone" },
            { id: 12, text: "Oil is dangerous because it destroys the ______ provided by the fur.", answer: "insulation" },
            { id: 13, text: "Some otters have been infected by parasites from ______ faeces.", answer: ["cat", "cats"] }
          ]
        }
      ]
    },
    {
      id: "p2",
      title: "Passage 2: How Habits Are Formed",
      text: `(A) Much of what we do each day is not the result of a conscious decision. In one well-known study, the psychologist Wendy Wood, of the University of Southern California, asked participants to record what they were doing and thinking every hour. She found that about 43 percent of their daily actions were performed while they were thinking about something else entirely. In other words, almost half of our behaviour is carried out by habit.

(B) Research on animals has helped to explain what happens in the brain as a habit forms. In the 1990s, the neuroscientist Ann Graybiel and her colleagues at the Massachusetts Institute of Technology recorded the brain activity of rats as they learned to run through a maze to find chocolate. At first, activity was high throughout each run, particularly in the areas of the brain responsible for decision-making. As the rats learned the route, activity in these areas fell, and a deeper structure called the basal ganglia took over. Activity became concentrated at the beginning and end of each run, as if the whole journey had become a single 'chunk' of behaviour.

(C) Researchers now commonly describe habits as a loop with three parts. First, there is a cue: a signal that triggers the behaviour, such as a time of day, a place, an emotion, other people, or a preceding action. Next comes the routine, which is the behaviour itself. Finally, there is a reward, which helps the brain decide whether this particular loop is worth remembering. Over time, the brain begins to expect the reward as soon as it notices the cue, producing a craving that drives the behaviour.

(D) It is often said that a new habit takes 21 days to form. This figure can be traced back to a book published in 1960 by Maxwell Maltz, a plastic surgeon, who observed that his patients took about three weeks to get used to their new appearance. He was not writing about habits at all. In 2009, Phillippa Lally and her colleagues at University College London carried out the first detailed study of the question. They asked 96 volunteers to choose a new behaviour, such as eating a piece of fruit at lunch or going for a run before dinner, and to report each day how automatic it felt. On average, it took 66 days for the behaviour to become automatic, but the range was enormous, from 18 to 254 days. Encouragingly, missing a single day did not seriously affect the process.

(E) Breaking an existing habit is generally harder than forming a new one. One reason is that the connections between cue and routine remain in the brain long after a person has stopped the behaviour, which is why old habits can return so easily in times of stress. For this reason, many researchers believe that simply trying to stop a habit is less effective than keeping the cue and the reward but changing the routine. Someone who eats biscuits when they feel bored in the afternoon, for example, might instead take a short walk at the same time.

(F) Major changes in a person's life can also provide an opportunity. Wood studied students who were transferring to a new university and found that their old habits, such as exercising or watching television, continued only when the new setting contained similar cues to the old one. When the cues were missing, the habits often disappeared. Wood argues that moving house, starting a new job or other changes in circumstances create a short period in which it is easier than usual to establish better habits.

(G) These findings have practical uses beyond the lives of individuals. Governments have used them to encourage saving, for example by enrolling workers in pension schemes automatically, so that saving becomes the default rather than something people must choose to do. Similarly, some companies have found that placing healthy foods at eye level in staff canteens increases the amount of fruit and vegetables that employees eat, without any need to persuade them.`,
      questions: [
        {
          id: "q14_18",
          instruction: "Questions 14-18: Reading Passage 2 has seven paragraphs, A-G. Choose the correct heading for paragraphs B-F from the list of headings below.",
          type: "matching_select",
          legend: [
            "i. Why stopping a habit is so difficult",
            "ii. The role of genes in forming habits",
            "iii. The brain gradually hands control to a different area",
            "iv. Challenging a popular belief about timing",
            "v. How companies take advantage of bad habits",
            "vi. A simple three-part pattern",
            "vii. Using life changes to make a fresh start",
            "viii. Why habits are more common in older people"
          ],
          options: ["i", "ii", "iii", "iv", "v", "vi", "vii", "viii"],
          items: [
            { id: 14, text: "Paragraph B", answer: "iii" },
            { id: 15, text: "Paragraph C", answer: "vi" },
            { id: 16, text: "Paragraph D", answer: "iv" },
            { id: 17, text: "Paragraph E", answer: "i" },
            { id: 18, text: "Paragraph F", answer: "vii" }
          ]
        },
        {
          id: "q19_22",
          instruction: "Questions 19-22: Match each finding with the correct researcher, A, B or C.\nNB You may use any letter more than once.",
          type: "matching_select",
          legend: ["A. Wendy Wood", "B. Ann Graybiel", "C. Phillippa Lally"],
          options: ["A", "B", "C"],
          items: [
            { id: 19, text: "Forming a new habit can take more than eight months.", answer: "C" },
            { id: 20, text: "A change of setting can cause old habits to disappear.", answer: "A" },
            { id: 21, text: "As a routine is learned, brain activity becomes focused on its beginning and end.", answer: "B" },
            { id: 22, text: "Almost half of everyday behaviour is habitual.", answer: "A" }
          ]
        },
        {
          id: "q23_26",
          instruction: "Questions 23-26: Complete the sentences below. Write NO MORE THAN TWO WORDS from the passage for each answer.",
          type: "fill_blank",
          items: [
            { id: 23, text: "Once a habit is established, noticing the cue produces a ______ for the reward.", answer: "craving" },
            { id: 24, text: "The idea that habits take 21 days to form came from a ______.", answer: ["plastic surgeon", "surgeon"] },
            { id: 25, text: "In Lally's study, missing a single ______ did not seriously affect habit formation.", answer: "day" },
            { id: 26, text: "To break a habit, it may be best to keep the cue and the reward but change the ______.", answer: "routine" }
          ]
        }
      ]
    },
    {
      id: "p3",
      title: "Passage 3: Vertical Farming — Promise and Problems",
      text: `(A) By 2050, the world's population is expected to reach almost ten billion, and around two-thirds of those people will live in cities. Feeding them will require a large increase in food production, yet farmland is being lost every year to urban growth, soil degradation and the effects of climate change. One proposed solution is to grow crops indoors, in stacked layers, inside buildings in the cities themselves. This approach is known as vertical farming.

(B) The idea was popularised by Dickson Despommier, a professor at Columbia University in New York. In 1999, he set his students the task of designing a farm inside a skyscraper that could feed 50,000 people. No such building was ever constructed, but the concept attracted considerable attention from architects and investors. The first commercial vertical farms appeared in the early 2010s in Japan and Singapore, two places where land is both scarce and expensive.

(C) In a vertical farm, plants are grown without soil. In hydroponic systems, their roots sit in water containing all the nutrients they need; in aeroponic systems, the roots hang in the air and are sprayed regularly with a nutrient-rich mist. Instead of sunlight, the plants receive light from LED lamps, which are tuned to the wavelengths that plants use most, mainly red and blue. Temperature, humidity and carbon dioxide levels are all controlled by computer. Because the growing rooms are sealed, pests cannot get in, so no pesticides are needed. Water is collected and reused, and as a result vertical farms use up to 95 percent less water than conventional farms.

(D) Supporters point to several further advantages. Crops can be grown all year round, regardless of the weather, and the yield from each square metre of floor space can be many times higher than that from a field. Because the farms are located in or near cities, produce can reach shops within hours of being harvested. This reduces transport costs and means that less food is wasted before it reaches the consumer.

(E) The greatest problem, however, is energy. In a field, sunlight is free; in a vertical farm, every unit of light must be paid for, along with the energy needed for heating, cooling and pumping water. When electricity prices rose sharply in 2022, several well-known vertical farming companies in Europe and the United States went out of business. Moreover, unless the electricity comes from renewable sources, the environmental benefits of vertical farming may be cancelled out by the carbon emissions from power stations.

(F) The range of crops that can be grown profitably is also limited. Leafy greens, herbs and strawberries are well suited to vertical farms, because they grow quickly, take up little space and almost the whole plant can be sold. Staple crops such as wheat, rice and maize, which provide most of the calories that people eat, are a different matter. They need a great deal of light, take several months to mature, and much of the plant, such as the stalks, cannot be eaten.

(G) In my view, vertical farming will not replace conventional agriculture, and we should not expect it to. Its role is more likely to be a complementary one, supplying fresh salad and herbs to cities, particularly in places with harsh climates such as the Middle East, where growing vegetables outdoors is difficult. The technology is certainly improving: LED lamps have become much more efficient over the past decade, and costs are likely to fall further. Nevertheless, claims that skyscraper farms will one day feed the cities of the future should be treated with caution.`,
      questions: [
        {
          id: "q27_31",
          instruction: "Questions 27-31: Choose the correct letter, A, B, C or D.",
          type: "mcq",
          items: [
            {
              id: 27,
              text: "Despommier's original skyscraper farm was",
              options: [
                "A. a commercial farm in Japan.",
                "B. a design exercise for his students.",
                "C. a project paid for by the government.",
                "D. a study of land prices in New York."
              ],
              answer: "B"
            },
            {
              id: 28,
              text: "Why were the first commercial vertical farms built in Japan and Singapore?",
              options: [
                "A. The governments offered financial support.",
                "B. Land there is limited and costly.",
                "C. The climate is unsuitable for farming.",
                "D. There is strong demand for organic food."
              ],
              answer: "B"
            },
            {
              id: 29,
              text: "According to paragraph C, vertical farms do not need pesticides because",
              options: [
                "A. the plants are grown without soil.",
                "B. the LED lamps kill insects.",
                "C. pests are unable to enter the growing rooms.",
                "D. the crops are harvested very quickly."
              ],
              answer: "C"
            },
            {
              id: 30,
              text: "What caused several vertical farming companies to close?",
              options: [
                "A. a sharp rise in the cost of electricity",
                "B. competition from greenhouses",
                "C. a lack of customers in cities",
                "D. disappointing crop yields"
              ],
              answer: "A"
            },
            {
              id: 31,
              text: "One reason staple crops are unsuitable for vertical farms is that",
              options: [
                "A. they need too much water.",
                "B. a large part of the plant cannot be eaten.",
                "C. they are easily damaged by pests.",
                "D. they can only be grown in soil."
              ],
              answer: "B"
            }
          ]
        },
        {
          id: "q32_36",
          instruction: "Questions 32-36: Do the following statements agree with the claims of the writer in Reading Passage 3? Choose YES, NO or NOT GIVEN.",
          type: "mcq",
          items: [
            {
              id: 32,
              text: "Vertical farms use much less water than traditional farms.",
              options: ["YES", "NO", "NOT GIVEN"],
              answer: "YES"
            },
            {
              id: 33,
              text: "Vertical farming will eventually replace conventional agriculture.",
              options: ["YES", "NO", "NOT GIVEN"],
              answer: "NO"
            },
            {
              id: 34,
              text: "Vertical farms are especially useful in regions with harsh climates.",
              options: ["YES", "NO", "NOT GIVEN"],
              answer: "YES"
            },
            {
              id: 35,
              text: "Consumers are willing to pay more for vegetables grown in vertical farms.",
              options: ["YES", "NO", "NOT GIVEN"],
              answer: "NOT GIVEN"
            },
            {
              id: 36,
              text: "Most vertical farms already run on renewable electricity.",
              options: ["YES", "NO", "NOT GIVEN"],
              answer: "NOT GIVEN"
            }
          ]
        },
        {
          id: "q37_40",
          instruction: "Questions 37-40: Reading Passage 3 has seven paragraphs, A-G. Which paragraph contains the following information?\nNB You may use any letter more than once.",
          type: "matching_paragraph",
          options: ["A", "B", "C", "D", "E", "F", "G"],
          items: [
            { id: 37, text: "A reference to how quickly food can reach shops.", answer: "D" },
            { id: 38, text: "An explanation of how plants are fed without soil.", answer: "C" },
            { id: 39, text: "A prediction about the future size of the world's population.", answer: "A" },
            { id: 40, text: "An example of businesses that failed.", answer: "E" }
          ]
        }
      ]
    }
  ]
};

export default IELTS_MOCK4_READING;
